/**
 * ============================================================================
 * useCustomerInsights - CUSTOMER INSIGHTS DATA FETCHING HOOK
 * ============================================================================
 *
 * Production-ready TanStack Query hook for customer analytics
 * Supports both Spotts app and Manual booking customers
 *
 * OPTIMIZED: Search filtering is done client-side without triggering API calls
 * ============================================================================
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { vendorApi } from '../services/api';
import type {
  CustomerInsightsData,
  PeriodFilter,
  CustomerInsightsFilters,
  Customer,
  SourceFilter,
} from '../types/customerInsights';

interface CustomerInsightsResponse {
  insights: CustomerInsightsData;
  filteredCustomers: Customer[];
  totalCount: number;
}

interface RawInsightsResponse {
  insights: CustomerInsightsData;
  allCustomers: Customer[];
}

/**
 * Fetches customer insights with analytics data
 * @param venueId - The venue ID
 * @param period - Time period filter (week, month, quarter, year)
 * @param filters - Client-side filters (search, segment, type)
 * @param source - Booking source filter (all, spotts, manual)
 */
export function useCustomerInsights(
  venueId: number | undefined,
  period: PeriodFilter,
  filters: CustomerInsightsFilters,
  source: SourceFilter = 'all'
) {
  // Fetch raw data - only refetches when period/source changes
  // Search, segment, type filtering is done client-side for instant response
  const query = useQuery<RawInsightsResponse, Error>({
    // Only period and source trigger API calls
    // Segment, type, search are filtered client-side (no API cost)
    queryKey: ['customerInsights', venueId, period, source],

    queryFn: async () => {
      if (!venueId) throw new Error('Venue ID is required');

      // Fetch customer insights from API with source filter
      const insights = await vendorApi.getCustomerInsights(venueId, period, source);

      // Get all customers from response
      const allCustomers = insights.customers || insights.top_customers || [];

      return {
        insights,
        allCustomers,
      };
    },

    enabled: !!venueId,
    staleTime: 10 * 1000, // 10 seconds (production: need fresh data)
    gcTime: 5 * 60 * 1000, // 5 minutes in cache
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  // Client-side filtering - instant response, no API calls
  // This runs on every render when filters change, but uses cached data
  const filteredData = useMemo((): CustomerInsightsResponse | undefined => {
    if (!query.data) return undefined;

    const { insights, allCustomers } = query.data;
    let filteredCustomers = [...allCustomers];

    // Segment filter (client-side)
    if (filters.segment === 'member') {
      filteredCustomers = filteredCustomers.filter(
        (customer: Customer) => customer.has_membership
      );
    } else if (filters.segment !== 'all') {
      filteredCustomers = filteredCustomers.filter(
        (customer: Customer) => customer.segment === filters.segment
      );
    }

    // Type filter - new vs returning (client-side)
    if (filters.type !== 'all') {
      filteredCustomers = filteredCustomers.filter(
        (customer: Customer) =>
          filters.type === 'new' ? customer.is_new : !customer.is_new
      );
    }

    // Search filter (client-side) - instant, no debounce needed
    if (filters.search.trim()) {
      const searchQuery = filters.search.toLowerCase();
      filteredCustomers = filteredCustomers.filter(
        (customer: Customer) =>
          customer.name?.toLowerCase().includes(searchQuery) ||
          customer.email?.toLowerCase().includes(searchQuery) ||
          customer.phone?.includes(searchQuery)
      );
    }

    return {
      insights,
      filteredCustomers,
      totalCount: filteredCustomers.length,
    };
  }, [query.data, filters.segment, filters.type, filters.search]);

  // Return combined query state with filtered data
  return {
    data: filteredData,
    isPending: query.isPending,
    error: query.error,
    refetch: query.refetch,
    isRefetching: query.isRefetching,
  };
}
